<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import BrandMark from '@/components/layout/BrandMark.vue'
import LocaleSwitcher from '@/components/layout/LocaleSwitcher.vue'
import ThemeToggle from '@/components/layout/ThemeToggle.vue'
import { useAuthStore } from '@/stores/auth'

const { t } = useI18n()
const route = useRoute()
const auth = useAuthStore()

const catalog = computed(() =>
  ['courses', 'course-detail', 'public-course', 'journeys', 'journey-detail'].includes(
    String(route.name ?? ''),
  ),
)
const wide = computed(() => catalog.value || String(route.name ?? '').includes('player'))
const loginTo = computed(() => ({
  name: 'login' as const,
  query: { redirect: route.fullPath },
}))
const registerTo = computed(() => ({
  name: 'register' as const,
  query: { redirect: route.fullPath },
}))

/** The player keeps theme and language in its own sticky bar. */
const isPlayer = computed(() => route.name === 'course-player')
const showPreferences = computed(() => !isPlayer.value)

const nav = [
  { to: '/courses', labelKey: 'nav.courses' },
  { to: '/journeys', labelKey: 'nav.journeys' },
]
</script>

<template>
  <div class="flex min-h-full flex-col bg-stone-50 dark:bg-neutral-900">
    <header
      class="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-4 sm:px-6 dark:border-neutral-700 dark:bg-neutral-800"
    >
      <BrandMark academy collapse />
      <nav
        v-if="!isPlayer"
        class="ms-6 hidden items-center gap-1 sm:flex"
        :aria-label="t('nav.skipToContent')"
      >
        <RouterLink
          v-for="item in nav"
          :key="item.to"
          :to="item.to"
          class="rounded-lg px-3 py-2 text-sm font-medium text-gray-600 hover:bg-stone-100 dark:text-neutral-300 dark:hover:bg-neutral-700"
          active-class="bg-brand-50 text-brand-800 dark:bg-brand-950 dark:text-brand-200"
        >
          {{ t(item.labelKey) }}
        </RouterLink>
      </nav>
      <div class="flex flex-1 items-center justify-end gap-3">
        <template v-if="showPreferences">
          <ThemeToggle />
          <LocaleSwitcher />
        </template>
        <template v-if="!auth.isAuthenticated">
          <RouterLink
            :to="registerTo"
            class="text-sm font-medium text-gray-900 hover:text-gray-600 dark:text-neutral-100"
          >
            {{ t('auth.signUp') }}
          </RouterLink>
          <RouterLink
            :to="loginTo"
            class="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            {{ t('auth.signIn') }}
          </RouterLink>
        </template>
        <RouterLink
          v-else
          to="/dashboard"
          class="text-sm font-medium text-brand-700 hover:underline dark:text-brand-300"
        >
          {{ t('nav.dashboard') }}
        </RouterLink>
      </div>
    </header>
    <nav
      v-if="!isPlayer"
      class="flex gap-1 border-b border-gray-200 bg-white px-4 py-2 sm:hidden dark:border-neutral-700 dark:bg-neutral-800"
    >
      <RouterLink
        v-for="item in nav"
        :key="item.to"
        :to="item.to"
        class="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-neutral-300"
        active-class="bg-brand-50 text-brand-800 dark:bg-brand-950 dark:text-brand-200"
      >
        {{ t(item.labelKey) }}
      </RouterLink>
    </nav>

    <main
      class="mx-auto w-full flex-1"
      :class="
        wide && !catalog
          ? 'max-w-none px-0 py-0'
          : catalog
            ? 'max-w-7xl px-4 py-6 sm:px-6 lg:px-8'
            : 'max-w-3xl px-4 py-10 sm:px-6'
      "
    >
      <slot />
    </main>
  </div>
</template>
